import {
    Box, Container, Divider, Typography,
} from '@mui/material';
import {
    CheckCircleOutline,
    DescriptionOutlined,
    Inventory2Outlined,
    Memory,
    People,
    PhoneOutlined,
    ShowChart,
    Star,
    WhatsApp,
    WorkOutline,
} from '@mui/icons-material';
import { useState } from 'react';
import { LoggedInUser } from '../../../../types/auth.types';
import Anchor from '../../foundation/Anchor';
import Button from '../../foundation/Button';
import CustomBR from '../../foundation/CustomBR';
import { CenteredFlex } from '../../foundation/Flex';
import config from '../../config.client';
import ElpContactModal from './ElpContactModal';
import ElpFaq from './ElpFaq';
import ElpHowItWorks from './ElpHowItWorks';
import ElpPackagesModal from './ElpPackagesModal';
import ElpStats from './ElpStats';
import ElpStickyCtaMobile from './ElpStickyCtaMobile';
import ElpWhatsappDemo from './ElpWhatsappDemo';
import {
    ClosingCta,
    DemoSection,
    ElpBgSection,
    ElpCardSection,
    ElpContainer,
    FeatureCard,
    FeatureCardGrid,
    HeroBadge,
    HeroBg,
    HeroCtaRow,
    HeroHeadline,
    HeroSubText,
    LargeFeatureIconWrap,
    MatchScore,
    MidPageCta,
    SectionLabel,
    SectionSubtitle,
    SectionTitle,
} from './EmployerLanding.styles';

const DEMO_CANDIDATES = [
    {
        name: 'יובל כהן',
        role: 'מנהל מוצר תוכנה',
        location: 'תל אביב',
        score: 98,
        matchLabel: 'התאמה גבוהה',
        image: 'https://i.pravatar.cc/150?img=11',
    },
    {
        name: 'גלי לוי',
        role: 'מנהלת שיווק דיגיטלי',
        location: 'רעננה',
        score: 92,
        matchLabel: 'התאמה גבוהה',
        image: 'https://i.pravatar.cc/150?img=5',
    },
    {
        name: 'דניאל משה',
        role: 'מעצב UX/UI',
        location: 'ירושלים',
        score: 78,
        matchLabel: 'התאמה בינונית',
        scoreColor: '#8bc34a',
        image: 'https://i.pravatar.cc/150?img=12',
    },
    {
        name: 'רחל אברהם',
        role: 'מפתחת Full Stack',
        location: 'הרצליה',
        score: 95,
        matchLabel: 'התאמה גבוהה',
        image: 'https://i.pravatar.cc/150?img=44',
        desktopOnly: true,
    },
    {
        name: 'עמית שפירא',
        role: 'מנהל פרויקטים',
        location: 'נתניה',
        score: 88,
        matchLabel: 'התאמה גבוהה',
        image: 'https://i.pravatar.cc/150?img=15',
        desktopOnly: true,
    },
    {
        name: 'נועה ברק',
        role: 'ראש צוות R&D',
        location: 'פתח תקווה',
        score: 83,
        matchLabel: 'התאמה טובה',
        scoreColor: '#8bc34a',
        image: 'https://i.pravatar.cc/150?img=9',
        desktopOnly: true,
    },
    {
        name: 'תומר כץ',
        role: 'מהנדס DevOps בכיר',
        location: 'תל אביב',
        score: 76,
        matchLabel: 'התאמה בינונית',
        scoreColor: '#8bc34a',
        image: 'https://i.pravatar.cc/150?img=14',
        desktopOnly: true,
    },
];

const FEATURES = [
    {
        icon: <Memory sx={{ fontSize: '1.75rem' }} />,
        title: 'AI להתאמה מדויקת',
        text: 'האלגוריתם החכם שלנו מנתח מאות פרמטרים ומציג לך רק את המועמדים שבאמת מתאימים — לא רשימות ארוכות, רק התאמות אמיתיות.',
    },
    {
        icon: <ShowChart sx={{ fontSize: '1.75rem' }} />,
        title: 'שקיפות מלאה',
        text: 'ממשק חכם עם נתונים וסטטיסטיקות על חשיפת המשרה והגשות קורות חיים. תחליטו מבוסס נתונים, לא ניחושים.',
    },
    {
        icon: <WhatsApp sx={{ fontSize: '1.75rem' }} />,
        title: 'סוכן AI בוואטסאפ',
        text: 'מגיע גם לטאלנטים פאסיביים שאינם מחפשים עבודה אך פתוחים להזדמנות. הבוט עובד 24/7 ומרחיב את מאגר המועמדים.',
    },
];

const TRUST_CHIPS = ['חבילות גמישות', 'ניהול משרות פשוט', 'תוצאות תוך 48 שעות'];

export type EmployerLandingProps = {
    user?: LoggedInUser
}

const EmployerLanding = ({ user }: EmployerLandingProps): JSX.Element => {
    const [contactOpen, setContactOpen] = useState(false);
    const [packagesOpen, setPackagesOpen] = useState(false);

    const openEmployerRegistration = () => {
        if (user?.isEmployer) {
            window.location.assign('/backoffice');
            return;
        }
        const url = new URL(window.location.href);
        url.searchParams.set('redirect', '/backoffice');
        window.history.pushState({}, '', url.toString());
        window.modals?.toggleEmployerRegisterModal();
    };

    return (
        <>
            {/* ── Hero ───────────────────────────────────────────────── */}
            <HeroBg>
                <Container maxWidth="md" disableGutters sx={{ position: 'relative', zIndex: 1, px: { xs: 0, md: 0 } }}>
                    <HeroBadge>
                        <Star sx={{ fontSize: '0.85rem', color: '#86efac' }} />
                        גיוס חכם · מופעל על ידי AI
                    </HeroBadge>

                    <HeroHeadline>הדרך החכמה לגיוס עובדים</HeroHeadline>

                    <HeroSubText>
                        סוכן ה-AI האישי שלנו בוואטסאפ מאתר טאלנטים פאסיביים ומוודא התאמה מקסימלית.
                        {' '}אליכם יגיעו רק המועמדים האיכותיים והמתאימים ביותר לתפקיד.
                    </HeroSubText>

                    <HeroCtaRow>
                        <Button
                            size="large"
                            onClick={openEmployerRegistration}
                            sx={{
                                bgcolor: 'white',
                                color: 'primary.main',
                                fontWeight: 700,
                                px: 3.5,
                                '&:hover': { bgcolor: '#f0faf4' },
                            }}
                        >
                            התחילו לפרסם
                        </Button>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => setContactOpen(true)}
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.55)',
                                px: 3.5,
                                '&:hover': {
                                    borderColor: 'white',
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                },
                            }}
                        >
                            חזרו אליי
                        </Button>
                    </HeroCtaRow>

                    <Box display="flex" justifyContent="center" gap={{ xs: 2, sm: 3 }} flexWrap="wrap" mt={3}>
                        {TRUST_CHIPS.map(t => (
                            <Box key={t} display="flex" alignItems="center" gap={0.6}>
                                <CheckCircleOutline sx={{ fontSize: '0.95rem', color: '#86efac' }} />
                                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.82)' }}>{t}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Container>
            </HeroBg>

            {/* ── Main content ───────────────────────────────────────── */}
            <ElpContainer maxWidth="lg" sx={{ mt: 0, pt: { xs: 3, md: 5 }, pb: { xs: '5.5rem', md: 5 } }}>

                {/* Stats bar */}
                <ElpStats />

                <CustomBR times={3} />

                {/* ── Why Mploy? feature cards ─────────────────────── */}
                <Box textAlign="center" mb={4}>
                    <SectionLabel>היתרון שלנו</SectionLabel>
                    <SectionTitle>
                        למה&nbsp;
                        <Box component="span" color="primary.main">{config.companyName}?</Box>
                    </SectionTitle>
                    <SectionSubtitle variant="body1">
                        טכנולוגיה מתקדמת, שקיפות מלאה ונגישות מושלמת כדי לחבר אותך למועמדים הכי מתאימים.
                    </SectionSubtitle>
                </Box>

                <FeatureCardGrid>
                    {FEATURES.map(feature => (
                        <FeatureCard key={feature.title}>
                            <LargeFeatureIconWrap>{feature.icon}</LargeFeatureIconWrap>
                            <Typography color="secondary" fontWeight={700} variant="h6" mb={1}>
                                {feature.title}
                            </Typography>
                            <Typography color="secondary" variant="body2" sx={{ opacity: 0.75, lineHeight: 1.7 }}>
                                {feature.text}
                            </Typography>
                        </FeatureCard>
                    ))}
                </FeatureCardGrid>

                <CustomBR times={3} />

                {/* ── Demo: WhatsApp + AI Match ────────────────────── */}
                <Box textAlign="center" mb={4}>
                    <SectionLabel>הבוט שמשנה את הגיוס</SectionLabel>
                    <SectionTitle>ראו את זה בפעולה</SectionTitle>
                </Box>

                <DemoSection>
                    {/* WhatsApp demo iframe */}
                    <Box flex={1.3} minWidth={0}>
                        <ElpWhatsappDemo />
                    </Box>

                    {/* AI Match card — stretches to match demo height on desktop */}
                    <Box
                        flex={1}
                        minWidth={0}
                        sx={{ display: { xs: 'block', md: 'flex' }, flexDirection: 'column' }}
                    >
                        <ElpCardSection sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <CenteredFlex flexDirection="column">
                                <Typography color="secondary" fontWeight="bold" variant="h6">
                                    11 מועמדים רלוונטיים נמצאו לך
                                </Typography>
                                <Box display="flex" alignItems="center" gap={0.5} color="primary.main" mt={1}>
                                    <Star fontSize="small" color="primary" />
                                    <Typography color="primary" fontWeight="bold" variant="caption">AI MATCH</Typography>
                                    <Star fontSize="small" color="primary" />
                                </Box>
                            </CenteredFlex>
                            <Divider style={{ margin: '1rem 0' }} />

                            <Box sx={{ flex: 1 }}>
                                {DEMO_CANDIDATES.map(candidate => (
                                    <Box
                                        key={candidate.name}
                                        display={candidate.desktopOnly ? { xs: 'none', md: 'flex' } : 'flex'}
                                        alignItems="center"
                                        justifyContent="space-between"
                                        padding="0.5rem"
                                        borderRadius="0.75rem"
                                    >
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <img
                                                src={candidate.image}
                                                alt={candidate.name}
                                                style={{
                                                    width: 48, height: 48, borderRadius: '9999px', objectFit: 'cover',
                                                }}
                                            />
                                            <Box>
                                                <Typography color="secondary" fontWeight="bold" variant="body2">{candidate.name}</Typography>
                                                <Typography color="secondary" variant="caption" display="block">
                                                    {candidate.role}
                                                </Typography>
                                                <Typography color="secondary" variant="caption" display="block" sx={{ opacity: 0.65 }}>
                                                    📍 {candidate.location}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <CenteredFlex flexDirection="column">
                                            <MatchScore scoreColor={candidate.scoreColor}>{candidate.score}%</MatchScore>
                                            <Typography color="secondary" variant="caption">{candidate.matchLabel}</Typography>
                                        </CenteredFlex>
                                    </Box>
                                ))}
                            </Box>

                            <Divider style={{ margin: '1rem 0' }} />
                            <CenteredFlex>
                                <Anchor href="/backoffice" text="צפו בכל המועמדים" />
                            </CenteredFlex>
                        </ElpCardSection>
                    </Box>
                </DemoSection>

                <CustomBR times={3} />

                {/* ── How it works ─────────────────────────────────── */}
                <ElpBgSection>
                    <ElpHowItWorks />
                </ElpBgSection>

                <CustomBR times={3} />

                {/* ── Mid-page CTA ─────────────────────────────────── */}
                <MidPageCta>
                    <SectionLabel>התחילו עכשיו</SectionLabel>
                    <SectionTitle sx={{ mb: 1.5 }}>
                        מוכנים לגייס חכם יותר?
                    </SectionTitle>
                    <SectionSubtitle variant="body1" sx={{ mb: 3.5 }}>
                        פרסמו משרה תוך דקות וקבלו מועמדים מתאימים תוך 48 שעות.
                    </SectionSubtitle>
                    <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="center" gap={2} flexWrap="wrap">
                        <Button color="primary" size="large" onClick={openEmployerRegistration} sx={{ px: 3.5, fontWeight: 700 }}>
                            התחילו לפרסם
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => setPackagesOpen(true)}
                            startIcon={<Inventory2Outlined />}
                        >
                            חבילות פרסום
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => setContactOpen(true)}
                            startIcon={<PhoneOutlined />}
                        >
                            צרו קשר
                        </Button>
                    </Box>
                </MidPageCta>

                <CustomBR times={3} />

                {/* ── FAQ ──────────────────────────────────────────── */}
                <Box textAlign="center" mb={3}>
                    <SectionLabel>שאלות נפוצות</SectionLabel>
                    <SectionTitle>כל מה שרציתם לדעת</SectionTitle>
                </Box>
                <ElpFaq />

                <CustomBR times={3} />

                {/* ── Closing CTA ──────────────────────────────────── */}
                <ClosingCta>
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                color: 'white', fontWeight: 800, mb: 1.5, letterSpacing: '-0.025em',
                            }}
                        >
                            הצטרפו לאמפלוי היום
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.65)', mb: 4, fontSize: '1rem' }}>
                            שניות להרשמה · תוצאות תוך 48 שעות · ביטול בכל עת
                        </Typography>
                        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="center" gap={2}>
                            <Button
                                size="large"
                                onClick={openEmployerRegistration}
                                sx={{
                                    bgcolor: 'white',
                                    color: 'primary.main',
                                    fontWeight: 700,
                                    px: 4,
                                    '&:hover': { bgcolor: '#f0faf4' },
                                }}
                            >
                                התחילו לפרסם
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => setContactOpen(true)}
                                sx={{
                                    color: 'white',
                                    borderColor: 'rgba(255,255,255,0.55)',
                                    px: 4,
                                    '&:hover': {
                                        borderColor: 'white',
                                        bgcolor: 'rgba(255,255,255,0.08)',
                                    },
                                }}
                            >
                                חזרו אליי
                            </Button>
                        </Box>

                        {/* Mini stats reassurance */}
                        <Box display="flex" justifyContent="center" gap={{ xs: 3, sm: 5 }} mt={4} flexWrap="wrap">
                            {[
                                { icon: <People sx={{ fontSize: '1.1rem' }} />, label: '200,000+ כניסות של מועמדים בחודש' },
                                { icon: <WorkOutline sx={{ fontSize: '1.1rem' }} />, label: 'מעל 3,000 מעסיקים מוצגים' },
                                { icon: <DescriptionOutlined sx={{ fontSize: '1.1rem' }} />, label: 'אלפי קורות חיים נשלחים כל יום' },
                            ].map(item => (
                                <Box key={item.label} display="flex" alignItems="center" gap={0.75}>
                                    <Box sx={{ color: '#86efac', display: 'flex' }}>{item.icon}</Box>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem' }}>
                                        {item.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </ClosingCta>

            </ElpContainer>

            <ElpContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
            <ElpPackagesModal open={packagesOpen} onClose={() => setPackagesOpen(false)} />
            <ElpStickyCtaMobile onClick={openEmployerRegistration} />
        </>
    );
};

export default EmployerLanding;
